import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase-config';

const millis = (value) => value?.toMillis?.() || (value ? new Date(value).getTime() : 0) || 0;
const dateOf = (value) => (millis(value) ? new Date(millis(value)).toLocaleDateString() : '');
const newestFirst = (a, b) => b.time - a.time;
const plain = (html = '') => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
const GROUPS = {
  general: 'General discussion', recipes: 'Recipe sharing', troubleshooting: 'Troubleshooting', ingredients: 'Ingredients',
  business: 'Business talk', showcase: 'Showcase', beginners: 'Beginner help', techniques: 'Techniques',
};

/**
 * Everything a member has shared publicly: recipes in the Recipe Collection, forum posts and
 * replies, and blog articles (only the SoapLab team writes those). Each source loads on its own,
 * so one failing query never blanks the whole profile.
 */
export function useProfileActivity(uid) {
  const [activity, setActivity] = useState({ loading: true, recipes: [], forumPosts: [], replies: [], articles: [] });

  useEffect(() => {
    if (!uid) {
      setActivity((prev) => ({ ...prev, loading: false }));
      return undefined;
    }
    let active = true;
    setActivity((prev) => ({ ...prev, loading: true }));
    Promise.allSettled([
      getDocs(query(collection(db, 'publicRecipes'), where('authorId', '==', uid), where('isPublic', '==', true))),
      getDocs(query(collection(db, 'forumPosts'), where('authorId', '==', uid))),
      getDocs(collection(db, 'forumPosts')),
      getDocs(query(collection(db, 'blogPosts'), where('authorId', '==', uid), where('status', '==', 'published'))),
    ]).then(([recipeSnap, forumSnap, allForumSnap, articleSnap]) => {
      if (!active) return;
      const docs = (result) => (result.status === 'fulfilled' ? result.value.docs.map((d) => ({ id: d.id, ...d.data() })) : []);
      const replies = docs(allForumSnap).flatMap((post) => (post.replies || [])
        .filter((reply) => reply?.authorId === uid)
        .map((reply, index) => ({ id: `${post.id}-${index}`, postTitle: post.title || 'Untitled post', content: reply.content || '', time: millis(reply.createdAt) })));
      setActivity({
        loading: false,
        recipes: docs(recipeSnap).map((r) => ({ ...r, time: millis(r.createdAt) })).sort(newestFirst),
        forumPosts: docs(forumSnap).map((p) => ({ ...p, time: millis(p.createdAt) })).sort(newestFirst),
        replies: replies.sort(newestFirst),
        articles: docs(articleSnap).map((a) => ({ ...a, time: millis(a.publishedAt || a.createdAt) })).sort(newestFirst),
      });
    });
    return () => { active = false; };
  }, [uid]);

  return activity;
}

/**
 * The activity part of a profile. Only sections with something in them are shown; a profile
 * with nothing shared yet gets one friendly line (with next steps on your own profile).
 */
export default function ProfileActivity({ activity, isOwn, name }) {
  if (activity.loading) return <section className="sl-band profile-muted">Loading activity…</section>;
  const { recipes, forumPosts, replies, articles } = activity;

  if (recipes.length + forumPosts.length + replies.length + articles.length === 0) {
    return (
      <section className="sl-band">
        {isOwn ? (
          <>
            <h2 className="sl-subtitle">Nothing shared yet</h2>
            <p className="sl-lede">
              Recipes you share in the Recipe Collection and your conversations in the forum will appear here, for other soap makers to see.
            </p>
            <div className="sl-actions">
              <Link to="/calculator" className="sl-btn sl-btn-primary">Share a recipe</Link>
              <Link to="/community" className="sl-arrow-link">Visit the forum</Link>
            </div>
          </>
        ) : (
          <p className="sl-lede">{name} hasn’t shared any recipes or forum posts yet.</p>
        )}
      </section>
    );
  }

  const figures = [
    ['Shared recipes', recipes.length],
    ['Forum posts', forumPosts.length],
    ['Replies', replies.length],
    ...(articles.length ? [['Articles', articles.length]] : []),
  ];

  return (
    <>
      <section className="sl-band">
        <div className={`sl-rule-grid sl-figure-grid ${figures.length === 4 ? 'cols-4' : ''}`}>
          {figures.map(([label, value]) => (
            <div key={label}>
              <span className="sl-figure">{value}</span>
              <span className="sl-figure-label">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {recipes.length > 0 && (
        <section className="sl-band">
          <h2 className="sl-subtitle">Shared recipes</h2>
          <div className="sl-rule-grid">
            {recipes.map((recipe) => (
              <Link key={recipe.id} to="/recipes/public" className="sl-rule-cell">
                <h3>{recipe.name || 'Untitled recipe'}</h3>
                <p>{(recipe.items || recipe.oils || []).slice(0, 4).map((oil) => `${oil.name} ${Math.round(oil.pct || 0)}%`).join(', ')}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {forumPosts.length > 0 && (
        <section className="sl-band">
          <h2 className="sl-subtitle">Forum posts</h2>
          <ul className="profile-list">
            {forumPosts.map((post) => (
              <li key={post.id}>
                <Link to="/community">{post.title || 'Untitled post'}</Link>
                <span>{[GROUPS[post.group] || 'General discussion', dateOf(post.createdAt), `${post.replyCount || 0} ${post.replyCount === 1 ? 'reply' : 'replies'}`].filter(Boolean).join(' · ')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {replies.length > 0 && (
        <section className="sl-band">
          <h2 className="sl-subtitle">Replies</h2>
          <ul className="profile-list">
            {replies.map((reply) => (
              <li key={reply.id}>
                <Link to="/community">In “{reply.postTitle}”</Link>
                <span>{plain(reply.content).slice(0, 160)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {articles.length > 0 && (
        <section className="sl-band">
          <h2 className="sl-subtitle">Articles</h2>
          <ul className="profile-list">
            {articles.map((article) => (
              <li key={article.id}>
                <Link to={`/blog/${article.slug || article.id}`}>{article.title}</Link>
                <span>{[article.category, dateOf(article.publishedAt)].filter(Boolean).join(' · ')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
